import { AlertTriangle, CalendarDays, Coins, FileBarChart2, FileSignature, HandCoins, IdCard, Plus } from 'lucide-react';
import { Link } from 'react-router';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useState } from 'react';
import { useTableauDeBord } from '@/api/hooks';
import { cheminDossier, LIBELLE_ETAPE, LIBELLE_TYPE_DOSSIER } from '@/api/types';
import { accedeModule, peutEmettreDocuments } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { BlocPlan } from '@/components/plan-tableau';
import { BlocsResultats } from '@/components/resultats';
import { BlocProceduresTableau, BlocRhTableau } from '@/components/rh';
import { BlocHrmsTableau } from '@/components/hrms';
import { BlocPaieTableau } from '@/components/paie';
import { BlocPresencesTableau } from '@/components/presences';
import { BlocCalculPaieTableau } from '@/components/calcul-paie';
import { Alertes, DerniersDossiers, MesDocuments, NiveauxCircuit, SelecteurExercice } from '@/components/tableau-de-bord';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { heures, montant, pct } from '@/lib/utils';

/** Tableau de bord : « Vue globale → Analyse → Détail → Action ». Ce qui attend l'utilisateur d'abord. */
export function TableauDeBord() {
  const session = useSession();
  const [exercice, setExercice] = useState<number>(new Date().getFullYear());
  const { data, isLoading, error } = useTableauDeBord(exercice);
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const u = session!.utilisateur;
  const d = data.documents;
  const c = data.courrier;
  const b = data.budget.total;
  // Composition par profil (spécifications complémentaires v1.1, § 9) : seuls les blocs du profil apparaissent.
  const profils = u.profils;
  const a = (...p: string[]) => profils.some((x) => p.includes(x));
  const emetteur = peutEmettreDocuments(u.habilitations);
  const valideur = u.etapes.length > 0;
  const transversal = data.utilisateur.perimetre === 'FONDS';
  const voitDocuments = valideur || a('DIRECTEUR', 'CHEF_SERVICE', 'COORDONNATEUR_MEMORIAL', 'CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'ADMINISTRATEUR', 'RESPONSABLE_ACHATS', 'CELLULE_MARCHES', 'CHARGE_ACHATS');
  const voitNiveaux = a('DIRECTEUR_GENERAL', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_FINANCIER', 'CONTROLE_GESTION', 'ADMINISTRATEUR') && data.niveaux.length > 0;
  const voitCourrier = accedeModule(profils, 'courrier') || accedeModule(profils, 'instructions') || accedeModule(profils, 'factures') || Boolean(c.parapheur);
  const voitBudget = accedeModule(profils, 'budget');
  const voitATraiter = valideur || data.aTraiter.total > 0;
  const lienRetard = valideur ? '/tdr?onglet=a-traiter' : '/tdr?statut=SOUMIS';
  const graphe = data.budget.structures.filter((s) => s.budgetActualise > 0).map((s) => ({ nom: s.structureCode, Budget: s.budgetActualise, Engagé: s.engage, Payé: s.paye }));

  return (
    <div className="space-y-6">
      <PageHeader
        titre={`Bonjour ${u.prenom}`}
        sousTitre={
          <span className="flex flex-wrap items-center gap-1.5">
            {data.utilisateur.profils.map((p) => (
              <Badge key={p.code} variant="secondary">
                {p.libelle}
              </Badge>
            ))}
            <span>· périmètre : {data.utilisateur.perimetre === 'FONDS' ? 'tout le Fonds' : data.utilisateur.structures.join(', ')}</span>
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2" data-testid="actions-rapides">
            <SelecteurExercice exercice={exercice} onChange={setExercice} />
            {/* Démarches RH (spécifications v1.4, § 3.1 et 7.2) : espace personnel et demandes de tout agent, même sans profil (demande du DG du 4 novembre 2026 : les demandes de congé et de prêt sont des actions rapides pour tous, émetteurs compris). */}
            <Button asChild variant="outline">
              <Link to="/rh">
                <IdCard className="size-4" /> Mes démarches RH
              </Link>
            </Button>
            <Button asChild variant={emetteur ? 'outline' : 'default'}>
              <Link to="/rh/conges/nouvelle">
                <CalendarDays className="size-4" /> Demander un congé
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/rh/prets/nouvelle">
                <HandCoins className="size-4" /> Demander un prêt
              </Link>
            </Button>
            {emetteur && (
              <>
                <Button asChild>
                  <Link to="/tdr/nouveau">
                    <Plus className="size-4" /> Nouveau TDR
                  </Link>
                </Button>
                <Button asChild variant="secondary">
                  <Link to="/fed/nouveau">
                    <Plus className="size-4" /> Nouveau FED
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/requisitions/nouvelle">
                    <Plus className="size-4" /> Nouvelle réquisition
                  </Link>
                </Button>
                {/* Note explicative sur un TDR déjà soumis ou autorisé (GEDEM v3.0, § 7) : le TDR se choisit à l'étape suivante. */}
                <Button asChild variant="outline">
                  <Link to="/ne/nouvelle">
                    <FileSignature className="size-4" /> Nouvelle note
                  </Link>
                </Button>
              </>
            )}
            {u.profils.some((p) => ['DIRECTEUR_GENERAL', 'DGA_FINANCES', 'DGA_OPERATIONS', 'DIRECTEUR_FINANCIER', 'CONTROLE_GESTION', 'CHEF_COMPTABLE', 'ADMINISTRATEUR', 'DIRECTEUR'].includes(p)) && (
              <Button variant="outline" asChild>
                <Link to="/rapports">
                  <FileBarChart2 className="size-4" /> Rapports d'exécution
                </Link>
              </Button>
            )}
          </div>
        }
      />

      <Alertes alertes={data.alertes} />

      {data.rh && <BlocRhTableau bloc={data.rh} />}
      {data.rh?.procedures && <BlocProceduresTableau bloc={data.rh.procedures} />}
      {data.rh?.hrms && <BlocHrmsTableau bloc={data.rh.hrms} />}
      {data.rh?.paie && <BlocPaieTableau bloc={data.rh.paie} />}
      {data.rh?.presences && <BlocPresencesTableau bloc={data.rh.presences} />}
      {data.rh?.calculs && <BlocCalculPaieTableau bloc={data.rh.calculs} />}

      {emetteur && <MesDocuments mes={data.mesDocuments} delaiCible={data.delais.cible} />}

      {data.plan && <BlocPlan bloc={data.plan} />}

      {voitATraiter && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            À traiter
            <Badge>{data.aTraiter.total}</Badge>
            {data.aTraiter.enRetard > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="size-3" /> {data.aTraiter.enRetard} en retard
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Dossiers en attente de votre décision, les plus anciens en premier (délai cible : {data.delais.cible} h par étape).
            {data.aTraiter.total > 0 && ` TDR ${data.aTraiter.tdr} · FED ${data.aTraiter.fed} · notes ${data.aTraiter.note} · réquisitions ${data.aTraiter.req ?? 0} · bons de commande ${data.aTraiter.bc}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.aTraiter.dossiers.length === 0 ? (
            <Vide message="Aucun dossier n'attend votre décision." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dossier</TableHead>
                  <TableHead>Objet</TableHead>
                  <TableHead className="hidden md:table-cell">Émetteur</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="hidden sm:table-cell">Étape</TableHead>
                  <TableHead className="text-right">Attente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.aTraiter.dossiers.map((x) => (
                  <TableRow key={`${x.type}-${x.id}`}>
                    <TableCell>
                      <Link to={cheminDossier(x.type, x.id)} className="font-medium text-primary hover:underline">
                        {x.numero ?? LIBELLE_TYPE_DOSSIER[x.type]}
                      </Link>
                      <div className="text-xs text-muted-foreground">{LIBELLE_TYPE_DOSSIER[x.type]}</div>
                    </TableCell>
                    <TableCell className="max-w-[28ch] truncate">{x.objet}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {x.emetteur} · {x.structureCode}
                    </TableCell>
                    <TableCell className="num text-right">{montant(x.montant)}</TableCell>
                    <TableCell className="hidden sm:table-cell">{LIBELLE_ETAPE[x.etape]}</TableCell>
                    <TableCell className={`num text-right ${x.enRetard ? 'font-semibold text-red-700' : ''}`}>{heures(x.heures)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}

      {voitNiveaux && <NiveauxCircuit niveaux={data.niveaux} />}

      {voitCourrier && (
      <Card>
        <CardHeader>
          <CardTitle>Courrier, factures et instructions</CardTitle>
          <CardDescription>
            {c.parapheur ? 'Votre parapheur et le suivi des instructions.' : c.instructions.perimetre === 'TOUTES' ? 'Suivi du courrier entrant et des instructions du Directeur Général.' : 'Les instructions qui vous sont adressées et le courrier que vous suivez.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {c.parapheur && (
            <Link to="/parapheur">
              <Kpi label="Parapheur" valeur={c.parapheur.courriers + c.parapheur.factures + c.parapheur.reponses + c.parapheur.reaffectations} detail={`${c.parapheur.courriers} courriers · ${c.parapheur.factures} factures · ${c.parapheur.reponses} réponses · ${c.parapheur.reaffectations} réaffectations`} accent={c.parapheur.courriers + c.parapheur.factures > 0 ? 'alerte' : undefined} />
            </Link>
          )}
          {(c.aAnalyser > 0 || u.profils.includes('CONSEILLER_TECHNIQUE')) && (
            <Link to="/courrier">
              <Kpi label="Courriers à analyser" valeur={c.aAnalyser} detail="analyse du conseiller technique" accent={c.aAnalyser > 0 ? 'alerte' : undefined} />
            </Link>
          )}
          <Link to="/instructions">
            <Kpi label={c.instructions.perimetre === 'TOUTES' ? 'Instructions ouvertes' : 'Mes instructions'} valeur={c.instructions.nouvelles + c.instructions.enCours} detail={`${c.instructions.nouvelles} nouvelles · ${c.instructions.enCours} en cours · ${c.instructions.enRetard} en retard${c.instructions.aValider ? ` · ${c.instructions.aValider} à valider` : ''}`} accent={c.instructions.enRetard > 0 ? 'critique' : undefined} />
          </Link>
          {c.enregistrement && (
            <Link to="/courrier">
              <Kpi label="Courrier enregistré" valeur={c.enregistrement.enAnalyse + c.enregistrement.soumis + c.enregistrement.transmis} detail={`${c.enregistrement.enAnalyse} en analyse · ${c.enregistrement.soumis} au parapheur · ${c.enregistrement.transmis} transmis`} />
            </Link>
          )}
          {c.factures && (
            <Link to="/factures">
              <Kpi label="Factures en circuit" valeur={c.factures.enCircuit} detail={`${c.factures.prochesDelai} proches du délai de 30 jours`} accent={c.factures.prochesDelai > 0 ? 'alerte' : undefined} />
            </Link>
          )}
        </CardContent>
      </Card>
      )}

      {voitDocuments && (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="bloc-documents">
        <Link to="/tdr?statut=SOUMIS"><Kpi label="TDR en validation" valeur={d.tdr.SOUMIS ?? 0} detail={`${d.tdr.A_CORRIGER ?? 0} à corriger · ${d.tdr.AUTORISE ?? 0} autorisés · ${(d.tdr.REJETE ?? 0) + (d.tdr.ANNULE ?? 0)} rejetés ou annulés`} /></Link>
        <Link to="/fed?statut=SOUMIS"><Kpi label="FED en validation" valeur={d.fed.SOUMIS ?? 0} detail={`${d.fed.A_CORRIGER ?? 0} à corriger · ${d.fed.AUTORISE ?? 0} autorisés · ${(d.fed.REJETE ?? 0) + (d.fed.ANNULE ?? 0)} rejetés ou annulés`} /></Link>
        <Link to="/tdr?statut=AUTORISE"><Kpi label="Autorisés ce mois" valeur={d.autorisesCeMois} detail="TDR et FED" accent="ok" /></Link>
        <Link to={lienRetard}><Kpi label="Dossiers en retard" valeur={d.enRetard} detail={`plus de ${data.delais.cible} h à l'étape en cours`} accent={d.enRetard > 0 ? 'critique' : undefined} /></Link>
      </div>
      )}

      {voitBudget && (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="bloc-budget">
        <Link to="/budget"><Kpi label="Budget prévisionnel" valeur={montant(b.budgetActualise)} detail={`${b.lignes} lignes · exercice ${data.budget.exercice}${transversal ? '' : ` · ${data.utilisateur.structures.join(', ')}`}`} /></Link>
        <Link to="/budget"><Kpi label="Engagé" valeur={montant(b.engage)} detail={`${pct(b.tauxEngagement)} du budget`} /></Link>
        <Link to="/budget"><Kpi label="Payé" valeur={montant(b.paye)} detail={`${pct(b.tauxExecution)} d'exécution`} /></Link>
        <Link to="/budget"><Kpi label="Disponible" valeur={montant(b.disponible)} detail="budget prévisionnel moins engagé" accent={b.disponible < 0 ? 'critique' : undefined} /></Link>
        <Link to="/budget"><Kpi label="Disponible après circuit" valeur={montant(b.disponiblePrevisionnel)} detail={`après ${montant(b.enCircuit)} en circuit`} accent={b.disponiblePrevisionnel < 0 ? 'alerte' : undefined} /></Link>
      </div>
      )}

      {(voitBudget || voitDocuments || data.tresorerie) && (
      <div className="grid gap-6 lg:grid-cols-3">
        {voitBudget && transversal && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Budget par structure</CardTitle>
            <CardDescription>Budget prévisionnel, engagé et payé (USD).</CardDescription>
          </CardHeader>
          <CardContent>
            {graphe.length === 0 ? (
              <Vide message="Aucune ligne budgétaire sur votre périmètre." />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={graphe} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="nom" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => (v >= 1_000_000 ? `${Math.round(v / 1_000_000)} M` : v >= 1000 ? `${Math.round(v / 1000)} k` : String(v))} width={48} />
                    <Tooltip formatter={(v) => montant(Number(v))} />
                    <Legend />
                    <Bar dataKey="Budget" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Engagé" fill="var(--chart-3)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Payé" fill="var(--chart-2)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        <div className={voitBudget && transversal ? 'space-y-6' : 'space-y-6 lg:col-span-3 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0'}>
          {voitDocuments && (
          <Card>
            <CardHeader>
              <CardTitle>Délais de validation</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Kpi label="TDR" valeur={heures(data.delais.tdrHeuresMoyennes)} detail="soumission → autorisation" />
              <Kpi label="FED" valeur={heures(data.delais.fedHeuresMoyennes)} detail="soumission → autorisation" />
            </CardContent>
          </Card>
          )}
          {data.tresorerie && (
            <Card data-testid="bloc-tresorerie">
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  Trésorerie
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/tresorerie">Ouvrir</Link>
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>À payer</span>
                  <span className="num font-medium">
                    {data.tresorerie.aPayer} · {montant(data.tresorerie.montantAPayer)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Mis en paiement</span>
                  <span className="num font-medium">
                    {data.tresorerie.liquides} · {montant(data.tresorerie.montantLiquide)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Payés ce mois</span>
                  <span className="num font-medium">
                    {data.tresorerie.payesCeMois} · {montant(data.tresorerie.montantPayeCeMois)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      )}

      {data.resultats && <BlocsResultats tableau={data.resultats} compact={!a('DIRECTEUR_GENERAL', 'DGA_OPERATIONS', 'DGA_FINANCES')} />}

      <DerniersDossiers recents={data.recents} libelleType={LIBELLE_TYPE_DOSSIER} />
      {data.ressources && (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              Ressources et quotes-parts {data.ressources.exercice}
              {data.ressources.alertes.length > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="size-3" /> {data.ressources.alertes.length} quote(s)-part(s) impayée(s) à la clôture
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Encaissé {montant(data.ressources.totaux.encaisse)} sur {montant(data.ressources.totaux.prevu)} prévus ({pct(data.ressources.totaux.tauxRealisation)}) · taux de couverture des dépenses payées {pct(data.ressources.totaux.tauxCouverture)}.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 lg:grid-cols-[1fr_18rem]">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.ressources.trimestres.map((t) => ({ nom: t.libelle, Prévu: t.prevu, Encaissé: t.encaisse }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nom" />
                  <YAxis tickFormatter={(v: number) => (Math.abs(v) >= 1_000_000 ? `${(v / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M` : v.toLocaleString('fr-FR'))} width={56} />
                  <Tooltip formatter={(v) => montant(Number(v ?? 0))} />
                  <Legend />
                  <Bar dataKey="Prévu" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Encaissé" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3 text-sm">
              {(['CIA_VAR', 'ECOSYSTEME'] as const).map((b) => {
                const q = data.ressources!.quotesParts[b];
                return (
                  <div key={b} className="rounded-md border p-3">
                    <div className="font-medium">
                      {q.libelle} <span className="text-xs text-muted-foreground">· {q.taux} %</span>
                    </div>
                    <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="text-muted-foreground">Due</div>
                        <div className="num">{montant(q.due)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Payée</div>
                        <div className="num">{montant(q.payee)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Restant dû</div>
                        <div className={q.restantDu > 0.5 ? 'num font-semibold text-orange-700' : 'num'}>{montant(q.restantDu)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" asChild>
                <Link to="/ressources">
                  <Coins className="size-4" /> Ressources et quotes-parts
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
